import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];

async function downloadDoc(doc) {
  try {
    const res = await api.get(`/api/documents/${doc.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(res);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name || `nota-gastos-${doc.id}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert('Archivo no disponible');
  }
}

export default function NotaGastos() {
  const user = useAuthStore(s => s.user);
  if (!user) return null;
  return ROLES_ADMIN.includes(user.rol) ? <NotaGastosAdmin /> : <NotaGastosTecnico />;
}

// ── Vista admin / jefe / administrativo ──────────────────────────────────────

function NotaGastosAdmin() {
  const [employees,     setEmployees]     = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [docs,          setDocs]          = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState('');
  const [regenerating,  setRegenerating]  = useState(false);
  const [regenMsg,      setRegenMsg]      = useState('');
  const [iframeReady,   setIframeReady]   = useState(false);
  const regenIframeRef = useRef(null);

  useEffect(() => {
    api.get('/api/employees/list').then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  // Regenera el Excel de una nota ya guardada, sin abrir el formulario ni
  // depender de que haya un empleado seleccionado en el iframe — todo lo
  // necesario viaja en el propio mensaje (ver nota-gastos.html: sgv_regen_nota).
  function regenOne(notaData) {
    return new Promise(resolve => {
      const win = regenIframeRef.current?.contentWindow;
      if (!win) return resolve(null);
      const reqId = Math.random().toString(36).slice(2);
      const timeout = setTimeout(() => { window.removeEventListener('message', onMsg); resolve(null); }, 30000);
      function onMsg(e) {
        if (!e.data || e.data.type !== 'sgv_regen_result' || e.data.reqId !== reqId) return;
        clearTimeout(timeout);
        window.removeEventListener('message', onMsg);
        resolve(e.data.fileB64 || null);
      }
      window.addEventListener('message', onMsg);
      win.postMessage({ type: 'sgv_regen_nota', notaData, reqId }, '*');
    });
  }

  async function regenerarHistoricos() {
    const candidatas = docs.filter(d => d.parte_data?.dias?.length);
    if (!candidatas.length) { setRegenMsg('No hay notas con datos para regenerar.'); return; }
    if (!window.confirm(
      `Esto regenera el Excel de ${candidatas.length} nota(s) de gastos con la fórmula actual de horas extra ` +
      `(jornada de 7h según el Calendario). Puede tardar varios minutos. ¿Continuar?`
    )) return;
    setRegenerating(true);
    let ok = 0, fail = 0;
    try {
      for (let i = 0; i < candidatas.length; i++) {
        const d = candidatas[i];
        setRegenMsg(`Regenerando ${i + 1}/${candidatas.length}…`);
        try {
          const fileB64 = await regenOne({
            empId: d.parte_data.empId,
            year:  d.año,
            sem:   d.semana,
            nombre: d.parte_data.nombre,
            dias:  d.parte_data.dias,
          });
          if (fileB64) {
            await api.patch(`/api/documents/${d.id}/file`, { file_b64: fileB64 });
            ok++;
          } else fail++;
        } catch { fail++; }
      }
      setRegenMsg(`Listo: ${ok} regenerada(s)${fail ? `, ${fail} con error` : ''}.`);
    } finally {
      setRegenerating(false);
    }
  }

  const selectedEmp  = employees.find(e => String(e.id) === String(selectedEmpId));
  const filterUserId = selectedEmp?.user_id || '';

  useEffect(() => {
    setLoading(true);
    if (selectedEmpId && !filterUserId) {
      setDocs([]);
      setLoading(false);
      return;
    }
    const url = filterUserId
      ? `/api/documents/all?user_id=${filterUserId}&tipo=nota_gastos`
      : '/api/documents/all?tipo=nota_gastos';
    api.get(url)
      .then(r => setDocs(r.data || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [selectedEmpId, filterUserId]);

  const filtered = search.trim()
    ? docs.filter(d => {
        const q = search.toLowerCase();
        return (
          String(d.semana  || '').includes(q) ||
          String(d.año     || '').includes(q) ||
          (d.nota_numero   ? String(d.nota_numero).toLowerCase().includes(q) : false) ||
          (d.nombre        || '').toLowerCase().includes(q) ||
          (d.user_nombre   || '').toLowerCase().includes(q)
        );
      })
    : docs;

  const totalGastos = docs.reduce((s, d) => s + (parseFloat(d.total_gastos) || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`@keyframes ngSpin { to { transform: rotate(360deg); } }`}</style>

      {/* Barra filtros */}
      <div style={{ padding: '10px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Empleado</span>
        <select value={selectedEmpId} onChange={e => { setSelectedEmpId(e.target.value); setSearch(''); }} style={SEL_S}>
          <option value="">— Todos —</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
        <input
          type="text"
          placeholder="Buscar por semana, año, nº nota o nombre…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', flex: 1, minWidth: 160 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
            {filtered.length}{search ? `/${docs.length}` : ''} nota{filtered.length !== 1 ? 's' : ''}
          </span>
          {totalGastos > 0 && (
            <span style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 800, whiteSpace: 'nowrap' }}>
              {totalGastos.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </span>
          )}
        </div>
        <button
          onClick={regenerarHistoricos}
          disabled={regenerating || !iframeReady || !docs.length}
          title="Regenera el Excel de las notas cargadas con la fórmula actual de horas extra"
          style={{ background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text-dim)', padding: '6px 10px', fontSize: 11, fontWeight: 700, cursor: regenerating || !iframeReady || !docs.length ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
        >
          {regenerating ? '⏳ Regenerando…' : '🔄 Regenerar históricos'}
        </button>
        {regenMsg && <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{regenMsg}</span>}
        <iframe
          ref={regenIframeRef}
          src="/nota-gastos.html"
          onLoad={() => setIframeReady(true)}
          title="regen-worker"
          style={{ position: 'fixed', left: -9999, top: 0, width: 900, height: 1200, border: 'none', opacity: 0, pointerEvents: 'none' }}
        />
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>
        ) : !filtered.length ? (
          <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>
            {search ? 'Sin resultados para esa búsqueda.' : 'Sin notas de gastos.'}
          </div>
        ) : (
          <div style={{ maxWidth: 860, margin: '0 auto', display: 'grid', gap: 6 }}>
            {filtered.map(d => <NotaRow key={d.id} d={d} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Vista técnico: iframe ─────────────────────────────────────────────────────

function NotaGastosTecnico() {
  useEffect(() => {
    function onMsg(e) {
      if (!e.data || e.data.type !== 'sgv_doc' || e.data.docType !== 'nota_gastos') return;
      const d = e.data;
      api.post('/api/documents/create', {
        tipo: 'nota_gastos',
        nombre: d.nombre || null,
        año: d.año || null,
        semana: d.semana || null,
        total_gastos: d.totalGastos || 0,
        nota_numero: d.notaNum || null,
        file_b64: d.fileB64 || null,
        file_name: d.fileName || null,
        parte_data: d.notaData || null,
      }).catch(() => {});

      if (Array.isArray(d.fotos) && d.fotos.length > 0) {
        d.fotos.forEach((f, idx) => {
          api.post('/api/documents/create', {
            tipo: 'factura',
            nombre: f.comentario ? `${f.comentario} – Sem ${d.semana}/${d.año}` : `Foto ${idx + 1} – Sem ${d.semana}/${d.año}`,
            año: d.año || null,
            semana: d.semana || null,
            total_gastos: 0,
            nota_numero: null,
            file_b64: f.b64 || null,
            file_name: f.name || `foto-${idx + 1}.jpg`,
          }).catch(() => {});
        });
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <iframe
      src="/nota-gastos.html"
      title="Nota de Gastos"
      style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
    />
  );
}

// ── Columnas de la tabla inline ───────────────────────────────────────────────
// Mapa real del template (row 10 del xlsx):
//   H="½" (media dieta, Python pone 1 si tot≥9,5h sin pernocta)
//   I="D"  (dieta completa/pernocta, Python pone 1 si ovn)
const TABLA_COLS = [
  { idx: 0,  label: 'Fecha',       align: 'left',   w: 70  },
  { idx: 1,  label: 'Instalación', align: 'left',   w: null },
  { idx: 2,  label: 'Nº',          align: 'center', w: 46  },
  { idx: 3,  label: 'M.O.',        align: 'center', w: 46  },
  { idx: 4,  label: 'Desp.',       align: 'center', w: 46  },
  { idx: 7,  label: '½ Dieta',     align: 'center', w: 54  },
  { idx: 8,  label: 'Dieta',       align: 'center', w: 46  },
  { idx: 9,  label: 'Hotel',       align: 'right',  w: 62  },
  { idx: 10, label: 'Vehículo',    align: 'right',  w: 62  },
  { idx: 11, label: 'Material',    align: 'right',  w: 62  },
];

const TIPO_BADGE = {
  'FESTIVO':    { bg: 'rgba(96,165,250,.12)',  color: '#60a5fa',  border: 'rgba(96,165,250,.3)'  },
  'VACACIONES': { bg: 'rgba(192,132,252,.12)', color: '#c084fc',  border: 'rgba(192,132,252,.3)' },
  'HORAS COMP.':{ bg: 'rgba(52,211,153,.12)',  color: '#34d399',  border: 'rgba(52,211,153,.3)'  },
};

// ── NotaRow ───────────────────────────────────────────────────────────────────

function NotaRow({ d }) {
  const [open,        setOpen]        = useState(false);
  const [fetching,    setFetching]    = useState(false);
  const [tableRows,   setTableRows]   = useState(null);
  const [horasExtras, setHorasExtras] = useState(0);
  const gastos = parseFloat(d.total_gastos) || 0;

  async function toggle() {
    if (open) { setOpen(false); return; }
    if (tableRows !== null) { setOpen(true); return; }

    if (!d.has_file) { setTableRows([]); setOpen(true); return; }

    setFetching(true);
    try {
      const blob = await api.get(`/api/documents/${d.id}/file`, { responseType: 'blob' });
      const ab  = await blob.arrayBuffer();
      const wb  = XLSX.read(new Uint8Array(ab), { type: 'array' });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

      // Filas de datos: col A tiene fecha (DD-mes) o col B tiene tipo especial
      const DATE_PAT = /^\d{1,2}[-/]/;
      const SPECIAL  = new Set(['FESTIVO', 'VACACIONES', 'HORAS COMP.']);
      const data = raw.filter(r => {
        const a = String(r?.[0] ?? '');
        const b = String(r?.[1] ?? '').toUpperCase().trim();
        return DATE_PAT.test(a) || (a && SPECIAL.has(b));
      });

      // El template ya tiene las columnas correctas escritas por Python:
      //   col H (idx 7) = ½ Dieta — Python pone 1 cuando tot≥9,5h sin pernocta
      //   col I (idx 8) = Dieta   — Python pone 1 cuando hay pernocta (ovn)
      // No se necesita cálculo virtual; se leen directamente del Excel.
      const augmented = data;

      // Horas extras: buscar celda A37 que contiene "X,X HORAS EXTRAS"
      const extrasCell = raw[36]?.[0];
      const extrasMatch = typeof extrasCell === 'string'
        ? extrasCell.match(/(\d+[,.]\d*|\d+)\s*HORAS EXTRAS/i)
        : null;
      const extras = extrasMatch ? parseFloat(extrasMatch[1].replace(',', '.')) : 0;

      setTableRows(augmented);
      setHorasExtras(extras);
    } catch (err) {
      console.error('[NotaGastos] parse error', err);
      setTableRows([]);
    }
    setFetching(false);
    setOpen(true);
  }

  // Sumatorio de columnas numéricas + conteos de dietas
  const totals = tableRows?.length
    ? (() => {
        const acc = {};
        [3, 4, 9, 10, 11].forEach(idx => {
          acc[idx] = tableRows.reduce((s, r) => s + (parseFloat(r?.[idx] ?? 0) || 0), 0);
        });
        acc[7] = tableRows.reduce((s, r) => s + (parseFloat(r?.[7] ?? 0) > 0 ? 1 : 0), 0); // ½ dietas
        acc[8] = tableRows.reduce((s, r) => s + (parseFloat(r?.[8] ?? 0) > 0 ? 1 : 0), 0); // dietas
        return acc;
      })()
    : null;

  return (
    <div style={{
      background: 'var(--surface-deep)',
      border: `1px solid ${open ? 'rgba(245,158,11,.45)' : 'var(--border)'}`,
      borderRadius: 8,
      overflow: 'hidden',
      transition: 'border-color .18s',
    }}>
      {/* ── Cabecera clicable ── */}
      <div
        onClick={toggle}
        style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            {d.semana && d.año && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, letterSpacing: '.04em', background: 'rgba(245,158,11,.12)', color: 'var(--amber)', border: '1px solid rgba(245,158,11,.3)' }}>
                Sem {d.semana} / {d.año}
              </span>
            )}
            {d.nota_numero && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, background: 'rgba(139,92,246,.12)', color: '#a78bfa', border: '1px solid rgba(139,92,246,.3)' }}>
                Nº {d.nota_numero}
              </span>
            )}
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.nombre || 'Nota de Gastos'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {d.user_nombre && <span style={{ color: '#a78bfa', fontWeight: 700 }}>{d.user_nombre}</span>}
            <span>{new Date(d.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            {gastos > 0 && (
              <span style={{ color: 'var(--amber)', fontWeight: 700 }}>
                {gastos.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {d.has_file && (
            <button
              onClick={e => { e.stopPropagation(); downloadDoc(d); }}
              title="Descargar Excel"
              style={{ background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700, lineHeight: 1 }}
            >
              ⬇ Excel
            </button>
          )}
          {/* Chevron */}
          <span style={{
            fontSize: 18, color: open ? 'var(--amber)' : 'var(--text-dim)',
            display: 'inline-block', transition: 'transform .2s, color .2s',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            lineHeight: 1,
          }}>›</span>
        </div>
      </div>

      {/* ── Panel expandible ── */}
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'rgba(0,0,0,.18)' }}>
          {fetching ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>
              <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid var(--border)', borderTopColor: 'var(--amber)', borderRadius: '50%', animation: 'ngSpin .7s linear infinite', verticalAlign: 'middle', marginRight: 7 }} />
              Cargando datos…
            </div>
          ) : !tableRows?.length ? (
            <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>
              {d.has_file ? 'El archivo no contiene filas de datos.' : 'No hay archivo adjunto para esta nota.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', padding: '0 0 2px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 620 }}>
                <thead>
                  <tr style={{ background: 'rgba(245,158,11,.06)' }}>
                    {TABLA_COLS.map(c => (
                      <th key={c.idx} style={{
                        padding: '7px 10px', textAlign: c.align, width: c.w || undefined,
                        color: 'var(--amber)', fontSize: 10, fontWeight: 800,
                        letterSpacing: '.06em', textTransform: 'uppercase',
                        borderBottom: '1px solid rgba(245,158,11,.2)', whiteSpace: 'nowrap',
                      }}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => {
                    const tipo = String(row[1] || '').toUpperCase();
                    const badge = TIPO_BADGE[tipo];
                    const isSpecial = !!badge;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.025)' }}>
                        {TABLA_COLS.map(c => {
                          const raw = row?.[c.idx];
                          const val = (raw === '' || raw === 0 || raw === null || raw === undefined) ? null : raw;

                          // Instalación cell: show badge for special day types
                          if (c.idx === 1 && isSpecial) {
                            return (
                              <td key={c.idx} colSpan={TABLA_COLS.length - 1}
                                style={{ padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                                <span style={{
                                  display: 'inline-block', padding: '1px 8px', borderRadius: 4,
                                  fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
                                  background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
                                }}>
                                  {tipo}
                                </span>
                              </td>
                            );
                          }
                          // Skip remaining cols if special (already handled with colSpan)
                          if (isSpecial && c.idx !== 0) return null;

                          // Columnas booleanas (½ dieta, dieta): checkmark o —
                          if ((c.idx === 7 || c.idx === 8) && val) {
                            const chkColor = c.idx === 7 ? '#fbbf24' : '#4ade80';
                            return (
                              <td key={c.idx} style={{ padding: '6px 10px', textAlign: c.align, borderBottom: '1px solid rgba(255,255,255,.04)', color: chkColor, fontWeight: 900, fontSize: 13 }}>✓</td>
                            );
                          }

                          const fmt = (c.idx >= 9 && val !== null)
                            ? Number(val).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                            : (val !== null ? String(val) : '—');

                          return (
                            <td key={c.idx} style={{
                              padding: '6px 10px', textAlign: c.align,
                              color: val === null ? 'var(--text-dim)' : 'var(--text)',
                              borderBottom: '1px solid rgba(255,255,255,.04)', whiteSpace: 'nowrap',
                            }}>
                              {fmt}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
                {/* Sumatorio */}
                {totals && (
                  <tfoot>
                    <tr style={{ background: 'rgba(245,158,11,.06)', borderTop: '2px solid rgba(245,158,11,.25)' }}>
                      {TABLA_COLS.map(c => {
                        if (c.idx === 0) return (
                          <td key={c.idx} style={{ padding: '7px 10px', fontSize: 10, fontWeight: 800, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>
                            Totales
                          </td>
                        );
                        if (c.idx === 1 || c.idx === 2) return <td key={c.idx} />;

                        const sum = totals[c.idx];
                        if (sum === undefined || sum === null) return <td key={c.idx} />;

                        // Columnas de conteo (½ dieta, dieta): mostrar como "N días"
                        if (c.idx === 7 || c.idx === 8) {
                          const color = c.idx === 7 ? '#fbbf24' : '#4ade80';
                          return (
                            <td key={c.idx} style={{ padding: '7px 10px', textAlign: c.align, fontWeight: 800, color: sum > 0 ? color : 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                              {sum > 0 ? `${sum} día${sum !== 1 ? 's' : ''}` : '—'}
                            </td>
                          );
                        }

                        // Columnas de horas (M.O., Desp.)
                        if (c.idx === 3 || c.idx === 4) {
                          return (
                            <td key={c.idx} style={{ padding: '7px 10px', textAlign: c.align, fontWeight: 800, color: sum > 0 ? 'var(--amber)' : 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                              {sum > 0 ? `${sum.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h` : '—'}
                            </td>
                          );
                        }

                        // Columnas de dinero
                        return (
                          <td key={c.idx} style={{ padding: '7px 10px', textAlign: c.align, fontWeight: 800, color: sum > 0 ? 'var(--amber)' : 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                            {sum > 0 ? `${sum.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—'}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Fila adicional: Horas extras de la semana */}
                    {horasExtras > 0 && (
                      <tr style={{ background: 'rgba(251,191,36,.05)' }}>
                        <td colSpan={TABLA_COLS.length} style={{ padding: '5px 10px', fontSize: 11, color: '#fbbf24', fontWeight: 700 }}>
                          ⏱ Horas extras semana: {horasExtras.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h
                        </td>
                      </tr>
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const SEL_S = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', minWidth: 160, maxWidth: 240 };
